import { useInvoiceDetails } from 'pages/invoices/hooks/useInvoiceDetails';
import { useInvoiceMatterForm } from 'pages/invoices/hooks/useInvoiceMatterForm';
import { actionsFirst } from 'pages/invoices/util/invoiceTabAlerts';
import { ReactNode } from 'react';
import { InvoiceData, InvoiceMatterDetailsDto } from 'types/invoices';
import { useHasPermissions } from 'utils/permissionUtil';
import RecalculatingOverlay from '../components/RecalculatingOverlay';
import { useIsMultiApprovalFlow } from '../views/details-tab/invoice-matters/useCaseApproval';
import { useInvoiceAlertNodes } from '../views/details-tab/InvoiceAlerts';
import { InvoiceTimecardsCard } from '../views/details-tab/invoice-timecards/InvoiceTimecardsCard';
import { byCaseNumber } from './invoiceSections';
import { InvoiceTabProps } from './invoiceTabProps';
import { InvoiceVariantNotice } from './InvoiceVariantNotice';

const UPDATE_APPROVED = ['UPDATE.INVOICES.STATE_APPROVED'];

/**
 * A case's items only read once the invoice does, or, on a joint invoice approved case by case, once this case is
 * approved and the user may not change an approved one: the case card's own rule.
 */
const useIsCaseReadOnly = (invoice: InvoiceData, invoiceMatter: InvoiceMatterDetailsDto, isInViewMode: boolean) => {
    const { isDetailsReadOnly } = useInvoiceDetails(invoice);
    const isMultiApproval = useIsMultiApprovalFlow(invoice);
    const canUpdateApproved = useHasPermissions(UPDATE_APPROVED);
    return isInViewMode || isDetailsReadOnly || (isMultiApproval && !!invoiceMatter.approvedAt && !canUpdateApproved);
};

/** This tab's alerts, those with an action first, each drawn as the classic tab draws it. */
export const useTabAlerts = ({
    alerts,
    invoice,
    addNewInvoiceItems,
    setSuccessMessages,
    addedInvoiceTimecardIds,
    addedInvoiceExpenseIds,
}: InvoiceTabProps) => {
    const { nodes, isUpdatingTimecardsHourlyRates } = useInvoiceAlertNodes({
        invoice,
        addNewInvoiceItems,
        setSuccessMessages,
        addedTimecardCount: addedInvoiceTimecardIds.length,
        addedExpenseCount: addedInvoiceExpenseIds.length,
    });
    const shown = actionsFirst(alerts).flatMap((alert) => (nodes[alert.id] ? [nodes[alert.id]] : []));
    return {
        alertList: shown.length ? <div className="flex flex-col gap-2">{shown}</div> : null,
        isUpdatingTimecardsHourlyRates,
    };
};

/** What a case's card on an item tab takes from the case's form and from the tab, once the case's summary has loaded. */
export const useCaseItemsCard = (props: InvoiceTabProps, invoiceMatter: InvoiceMatterDetailsDto) => {
    const { invoice, classifiers, handleStatusLoadingState } = props;
    const { form, summaryData, isMatterSummaryFetching } = useInvoiceMatterForm({
        invoice,
        invoiceMatter,
        classifiers,
        handleStatusLoadingState,
    });
    const isReadOnly = useIsCaseReadOnly(invoice, invoiceMatter, props.isInViewMode);
    return summaryData
        ? {
              invoice,
              invoiceMatter: summaryData,
              classifiers,
              isInViewMode: isReadOnly,
              successMessages: props.successMessages,
              setSuccessMessages: props.setSuccessMessages,
              validateForm: form.handleSubmit,
              getValues: form.getValues,
              setValue: form.setValue,
              reset: form.reset,
              control: form.control,
              isMatterSummaryFetching,
          }
        : null;
};

/** A case's card on an item tab, veiled as its case card is while totals recalculate or its rows reload. */
export function CaseItemsVeil({
    invoiceId,
    isFetching,
    children,
}: {
    invoiceId: number;
    isFetching: boolean;
    children: ReactNode;
}) {
    return (
        <div className="relative">
            {children}
            <RecalculatingOverlay
                invoiceId={invoiceId}
                isAlsoRecalculating={isFetching}
                hideLabel
                className="rounded-[10px]"
            />
        </div>
    );
}

function CaseTimecards({
    invoiceMatter,
    isUpdatingRates,
    ...props
}: InvoiceTabProps & { invoiceMatter: InvoiceMatterDetailsDto; isUpdatingRates: boolean }) {
    const card = useCaseItemsCard(props, invoiceMatter);
    if (!card) return null;
    const { isMatterSummaryFetching, ...cardProps } = card;
    return (
        <CaseItemsVeil invoiceId={props.invoice.id} isFetching={isMatterSummaryFetching}>
            <InvoiceTimecardsCard
                {...cardProps}
                addedInvoiceTimecardIds={props.addedInvoiceTimecardIds}
                setAddedInvoiceTimecardIds={props.setAddedInvoiceTimecardIds}
                isLoading={props.isLoading || props.addNewInvoiceItems.isAddingNewTimecards || isUpdatingRates}
            />
        </CaseItemsVeil>
    );
}

/**
 * The Timecards tab of the new design: a credit note's or a correction's notice, its alerts, then each case's Timecards
 * card by case number.
 */
export function TimecardsTab(props: InvoiceTabProps) {
    const { alertList, isUpdatingTimecardsHourlyRates } = useTabAlerts(props);
    return (
        <>
            <InvoiceVariantNotice invoice={props.invoice} />
            {alertList}
            {(props.invoice.invoiceMatters ?? []).toSorted(byCaseNumber).map((invoiceMatter) => (
                <CaseTimecards
                    key={invoiceMatter.matterId}
                    {...props}
                    invoiceMatter={invoiceMatter}
                    isUpdatingRates={isUpdatingTimecardsHourlyRates}
                />
            ))}
        </>
    );
}
